import { useEffect, useRef } from "react";
import { GO_SHORTCUTS } from "../lib/nav";
import { isTypingTarget } from "../lib/keys";

// How long after "g" the second key still counts.
const CHORD_MS = 1200;

/**
 * The app's keyboard shortcuts:
 *   ⌘K / Ctrl+K  open or close the command palette (works while typing)
 *   /            open the command palette
 *   ?            the list of shortcuts
 *   n            a new screening
 *   g then a key go to a page (GO_SHORTCUTS)
 * Single keys are ignored while typing in a field and while a dialog is
 * open, so they never steal a letter from a form.
 *
 * `canOpen(path)` says whether the user's role opens a page; a shortcut to
 * a page they cannot open does nothing.
 */
export function useGlobalShortcuts({ onPalette, onShortcuts, navigate, canOpen }) {
  const handlers = useRef({ onPalette, onShortcuts, navigate, canOpen });
  handlers.current = { onPalette, onShortcuts, navigate, canOpen };

  useEffect(() => {
    let chordAt = 0;
    const go = (path) => {
      const h = handlers.current;
      if (path && (!h.canOpen || h.canOpen(path))) h.navigate(path);
    };
    const onKey = (e) => {
      const h = handlers.current;
      const key = typeof e.key === "string" ? e.key : "";
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && key.toLowerCase() === "k") {
        e.preventDefault();
        h.onPalette();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      if (typeof document !== "undefined" && document.querySelector('[role="dialog"], [role="alertdialog"]')) return;

      if (chordAt && Date.now() - chordAt < CHORD_MS) {
        chordAt = 0;
        const path = GO_SHORTCUTS[key.toLowerCase()];
        if (path) {
          e.preventDefault();
          go(path);
        }
        return;
      }
      chordAt = 0;
      if (key === "g") {
        chordAt = Date.now();
      } else if (key === "/") {
        e.preventDefault();
        h.onPalette();
      } else if (key === "?") {
        e.preventDefault();
        h.onShortcuts();
      } else if (key === "n") {
        e.preventDefault();
        go("/transactions/new");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
